import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FLOW_DATA, MRV_ACTION_DATA, MRV_NATURE_DATA, FARM_ACTION_DATA, EXPLANATIONS } from './data/flowdata';

import './App.css';


function App() {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 1024);
      };
  
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
  
    const [selectedItems, setSelectedItems] = useState({
      resilience: new Set(),
      nature: new Set(),
      actions: new Set()
    });
  
    const [hoveredItem, setHoveredItem] = useState(null);
    const [connections, setConnections] = useState([]);
    const [filteredView, setFilteredView] = useState(false);
    const [visibleItems, setVisibleItems] = useState({
      resilience: new Set(),
      nature: new Set(),
      actions: new Set()
    });
    const [showExplanation, setShowExplanation] = useState(false);
    const [explanationText, setExplanationText] = useState('');
    const [explanationPosition, setExplanationPosition] = useState({ x: 0, y: 0 });
  
    const [farmPlan, setFarmPlan] = useState([]);
    const [selectedSAIQ, setSelectedSAIQ] = useState('');
    const [selectedAECS, setSelectedAECS] = useState('');
    const containerRef = useRef(null);
  
    useEffect(() => {
      if (!containerRef.current || isMobile) return;
    
      const calculateConnections = () => {
        const newConnections = [];
    
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect) return;
    
        // Nature ➝ Resilience connections
        FLOW_DATA.natureOutcomes.forEach(nature => {
          nature.connects.forEach(resilienceId => {
            const natureEl = document.getElementById(`nature-${nature.id}`);
            const resilienceEl = document.getElementById(`resilience-${resilienceId}`);
    
            if (natureEl && resilienceEl) {
              const natureRect = natureEl.getBoundingClientRect();
              const resilienceRect = resilienceEl.getBoundingClientRect();
    
              newConnections.push({
                id: `nature-${nature.id}-resilience-${resilienceId}`,
                startX: natureRect.right - containerRect.left,
                startY: natureRect.top - containerRect.top + natureRect.height / 2,
                endX: resilienceRect.left - containerRect.left,
                endY: resilienceRect.top - containerRect.top + resilienceRect.height / 2,
                type: 'nature-resilience',
                sourceId: nature.id,
                targetId: resilienceId,
                color: nature.color
              });
            }
          });
        });
    
        // Action ➝ Nature connections
        FLOW_DATA.farmActions.forEach(action => {
          action.connects.forEach(natureId => {
            const actionEl = document.getElementById(`action-${action.id}`);
            const natureEl = document.getElementById(`nature-${natureId}`);
    
            if (actionEl && natureEl) {
              const actionRect = actionEl.getBoundingClientRect();
              const natureRect = natureEl.getBoundingClientRect();
    
              newConnections.push({
                id: `action-${action.id}-nature-${natureId}`,
                startX: actionRect.right - containerRect.left,
                startY: actionRect.top - containerRect.top + actionRect.height / 2,
                endX: natureRect.left - containerRect.left,
                endY: natureRect.top - containerRect.top + natureRect.height / 2,
                type: 'action-nature',
                sourceId: action.id,
                targetId: natureId,
                color: '#666'
              });
            }
          });
        });
    
        setConnections(newConnections);
      };
    
      const timer = setTimeout(calculateConnections, 100);
      window.addEventListener('resize', calculateConnections);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', calculateConnections);
      };
    }, [visibleItems, isMobile]);
    
    const handleItemClick = (category, itemId) => {
      // Clear filters when selecting an item
      setSelectedSAIQ('');
      setSelectedAECS('');
      
      const isCurrentlySelected = selectedItems[category].has(itemId);
      if (isCurrentlySelected) {
        setSelectedItems({ resilience: new Set(), nature: new Set(), actions: new Set() });
        setFilteredView(false);
        setVisibleItems({ resilience: new Set(), nature: new Set(), actions: new Set() });
      } else {
        const newSelected = { resilience: new Set(), nature: new Set(), actions: new Set() };
        newSelected[category].add(itemId);
        const connected = getConnectedItems(category, itemId);
        setSelectedItems(newSelected);
        setFilteredView(true);
        setVisibleItems(connected);
      }
    };
  
    const getConnectedItems = (category, itemId) => {
      const connected = { resilience: new Set(), nature: new Set(), actions: new Set() };
      if (category === 'resilience') {
        connected.resilience.add(itemId);
        FLOW_DATA.natureOutcomes.forEach(nature => {
          if (nature.connects.includes(itemId)) {
            connected.nature.add(nature.id);
            FLOW_DATA.farmActions.forEach(action => {
              if (action.connects.includes(nature.id)) {
                connected.actions.add(action.id);
              }
            });
          }
        });
      }
      if (category === 'nature') {
        connected.nature.add(itemId);
        const nature = FLOW_DATA.natureOutcomes.find(n => n.id === itemId);
        if (nature) {
          nature.connects.forEach(resilienceId => connected.resilience.add(resilienceId));
        }
        FLOW_DATA.farmActions.forEach(action => {
          if (action.connects.includes(itemId)) connected.actions.add(action.id);
        });
      }
      if (category === 'actions') {
        connected.actions.add(itemId);
        const action = FLOW_DATA.farmActions.find(a => a.id === itemId);
        if (action) {
          action.connects.forEach(natureId => {
            connected.nature.add(natureId);
            const nature = FLOW_DATA.natureOutcomes.find(n => n.id === natureId);
            if (nature) {
              nature.connects.forEach(resilienceId => connected.resilience.add(resilienceId));
            }
          });
        }
      }
      return connected;
    };
  
    const isItemVisible = (category, itemId) => {
      // Handle combined AECS and SAIQ filters for actions
      if (!filteredView && (selectedAECS || selectedSAIQ) && category === 'actions') {
        const action = FARM_ACTION_DATA[itemId];
        if (!action) return false;
        
        let matchesAECS = true;
        let matchesSAIQ = true;
        
        // Check AECS filter if active
        if (selectedAECS) {
          if (selectedAECS === 'aecs') {
            matchesAECS = action.aecs === true;
          } else if (selectedAECS === 'non-aecs') {
            matchesAECS = action.aecs === false;
          }
        }
        
        // Check SAIQ filter if active
        if (selectedSAIQ) {
          matchesSAIQ = action.SAIQ && action.SAIQ.includes(selectedSAIQ);
        }
        
        // Both filters must match if both are active
        return matchesAECS && matchesSAIQ;
      }
  
  
      // Handle nature and resilience visibility with AECS or SAIQ filters
      if (!filteredView && (selectedSAIQ || selectedAECS)) {
        if (category === 'nature') {
          // Show nature outcomes connected to filtered actions
          return FLOW_DATA.farmActions.some(action => {
            const actionData = FARM_ACTION_DATA[action.id];
            if (!actionData) return false;
            
            let matchesAECS = true;
            let matchesSAIQ = true;
            
            // Check AECS filter if active
            if (selectedAECS) {
              if (selectedAECS === 'aecs') {
                matchesAECS = actionData.aecs === true;
              } else if (selectedAECS === 'non-aecs') {
                matchesAECS = actionData.aecs === false;
              }
            }
            
            // Check SAIQ filter if active
            if (selectedSAIQ) {
              matchesSAIQ = actionData.SAIQ && actionData.SAIQ.includes(selectedSAIQ);
            }
            
            // Both filters must match if both are active
            const hasMatchingFilter = matchesAECS && matchesSAIQ;
            return hasMatchingFilter && action.connects.includes(itemId);
          });
        }
        
        if (category === 'resilience') {
          // Show resilience outcomes connected to visible nature outcomes
          return FLOW_DATA.natureOutcomes.some(nature => {
            // Check if this nature outcome is visible (connected to filtered actions)
            const isNatureVisible = FLOW_DATA.farmActions.some(action => {
              const actionData = FARM_ACTION_DATA[action.id];
              if (!actionData) return false;
              
              let matchesAECS = true;
              let matchesSAIQ = true;
              
              // Check AECS filter if active
              if (selectedAECS) {
                if (selectedAECS === 'aecs') {
                  matchesAECS = actionData.aecs === true;
                } else if (selectedAECS === 'non-aecs') {
                  matchesAECS = actionData.aecs === false;
                }
              }
              
              // Check SAIQ filter if active
              if (selectedSAIQ) {
                matchesSAIQ = actionData.SAIQ && actionData.SAIQ.includes(selectedSAIQ);
              }
              
              // Both filters must match if both are active
              const hasMatchingFilter = matchesAECS && matchesSAIQ;
              
              return hasMatchingFilter && action.connects.includes(nature.id);
            });
            // If nature outcome is visible and connects to this resilience outcome
            return isNatureVisible && nature.connects.includes(itemId);
          });
        }
      }
      
      return !filteredView || visibleItems[category].has(itemId);
    };
  
    const isItemSelected = (category, itemId) =>
      selectedItems[category].has(itemId);
  
    const createBezierPath = ({ startX, startY, endX, endY }) => {
      const distance = Math.abs(endX - startX);
      const controlOffset = Math.min(distance * 0.6, 80);
      const controlX1 = startX + controlOffset;
      const controlX2 = endX - controlOffset;
      return `M ${startX} ${startY} C ${controlX1} ${startY}, ${controlX2} ${endY}, ${endX} ${endY}`;
    };
  
    const isConnectionHighlighted = (connection) => {
      if (!hoveredItem) return false;
      const { category, id } = hoveredItem;
      return (
        (category === 'resilience' && connection.targetId === id) ||
        (category === 'nature' && (connection.sourceId === id || connection.targetId === id)) ||
        (category === 'actions' && connection.sourceId === id)
      );
    };
  
    const getExplanation = (fromCategory, fromId, toCategory, toId) => {
      if (fromCategory === 'actions' && toCategory === 'nature')
        return EXPLANATIONS.action_nature[`${fromId}-${toId}`];
      if (fromCategory === 'nature' && toCategory === 'resilience')
        return EXPLANATIONS.nature_resilience[`${fromId}-${toId}`];
      return null;
    };
  
    const addToFarmPlan = (actionId) => {
      if (!farmPlan.find(item => item.id === actionId)) {
        const actionData = FARM_ACTION_DATA[actionId];
        const { additionalIncome, ...cleanActionData } = actionData; // Remove additionalIncome
        setFarmPlan([...farmPlan, {
          id: actionId,
          amount: 0, // Use 'amount' instead of 'hectares' to be unit-agnostic
          ...cleanActionData
        }]);
      }
    };
  
    const removeFromFarmPlan = (actionId) =>
      setFarmPlan(farmPlan.filter(item => item.id !== actionId));
  
    const updateAmount = (actionId, amount) => {
      setFarmPlan(farmPlan.map(item => {
        if (item.id === actionId) {
          const { additionalIncome, ...cleanItem } = item; // Remove additionalIncome if it exists
          return { ...cleanItem, amount: Math.max(0, amount) };
        }
        return item;
      }));
    };
  
    const calculateIncomeByType = useCallback((action, year, incomeType) => {
      if (!action[incomeType]) return 0;
      
      const amount = action.amount || 0;
      let effectiveAmount = amount;
      
      // Apply maximum area constraints if they exist for this income type
      // This limits how much of the total area/length is eligible for payment
      if (incomeType === 'aecs' && action.AECS_maxarea) {
        effectiveAmount = Math.min(amount, action.AECS_maxarea);
      }
      if (incomeType === 'carbon' && action.carbon_maxarea) {
        effectiveAmount = Math.min(amount, action.carbon_maxarea);
      }
      if (incomeType === 'nature' && action.nature_maxarea) {
        effectiveAmount = Math.min(amount, action.nature_maxarea);
      }
      if (incomeType === 'bps' && action.bps_maxarea) {
        effectiveAmount = Math.min(amount, action.bps_maxarea);
      }
      if (incomeType === 'fgs' && action.fgs_maxarea) {
        effectiveAmount = Math.min(amount, action.fgs_maxarea);
      }
      
      // Check if scheme has expired for this year
      const maxYears = action[`${incomeType === 'aecs' ? 'AECS' : incomeType}_maxyears`];
      if (maxYears && year > maxYears) {
        return 0;
      }
      
      // Handle different income types with year-specific payments
      switch(incomeType) {
        case 'aecs':
          return (action[`AECSpayment_yr${year}`] || action.AECSpayment_annual || 0) * effectiveAmount;
        case 'carbon':
          return (action[`carbonPayment_yr${year}`] || action.carbonPayment_annual || 0) * effectiveAmount;
        case 'nature':
          return (action[`naturePayment_yr${year}`] || action.naturePayment_annual || 0) * effectiveAmount;
        case 'bps':
          return (action[`bpsPayment_yr${year}`] || action.bpsPayment_annual || 0) * effectiveAmount;

        case 'fgs':
          return (action[`fgsPayment_yr${year}`] || action.bpsPayment_annual || 0) * effectiveAmount;
        default:
          return 0;
      }
    }, []);
  
    // Backward compatibility function for calculateAECSIncome
    // eslint-disable-next-line no-unused-vars
    const calculateAECSIncome = useCallback((action, year) => {
      return calculateIncomeByType(action, year, 'aecs');
    }, [calculateIncomeByType]);
  
    const getAllIncomeTypes = useCallback((action) => {
      const incomeTypes = [];
      
      if (action.aecs && (action.AECSpayment_yr1 || action.AECSpayment_annual)) {
        incomeTypes.push({
          type: 'aecs',
          label: 'AECS',
          maxArea: action.AECS_maxarea,
          maxYears: action.AECS_maxyears
        });
      }
      
      if (action.carbon && (action.carbonPayment_yr1 || action.carbonPayment_annual)) {
        incomeTypes.push({
          type: 'carbon',
          label: 'Carbon Credits',
          maxArea: action.carbon_maxarea,
          maxYears: action.carbon_maxyears
        });
      }
      
      if (action.nature && (action.naturePayment_yr1 || action.naturePayment_annual)) {
        incomeTypes.push({
          type: 'nature',
          label: 'Nature Credits',
          maxArea: action.nature_maxarea,
          maxYears: action.nature_maxyears
        });
      }
      
      if (action.bps && action.bpsPayment_annual) {
        incomeTypes.push({
          type: 'bps',
          label: 'Basic Payment Scheme',
          maxArea: action.bps_maxarea,
          maxYears: action.bps_maxyears
        });

        if (action.fgs && action.fgsPayment_annual) {
            incomeTypes.push({
              type: 'fgs',
              label: ' Forestry Grant Scheme',
              maxArea: action.fgs_maxarea,
              maxYears: action.fgs_maxyears
            });}
      }
      
      return incomeTypes;
    }, []);
  
    // Calculate break-even analysis
    const calculateBreakEven = useCallback((farmPlan) => {
        let cumulativeNetCashFlow = 0;
        let totalUpfrontCost = 0;
        let breakEvenYear = null;
        
        // Calculate total upfront implementation costs
        farmPlan.forEach(action => {
          totalUpfrontCost += (action.implementationCost || 0) * action.amount;
        });
        
        // Start with negative cash flow due to upfront costs
        cumulativeNetCashFlow = -totalUpfrontCost;
        
        for (let year = 1; year <= 10; year++) {
          let yearlyNetCashFlow = 0;
          
          farmPlan.forEach(action => {
            // Annual income sources
            let yearlyIncome = 0;
            
            // Cost savings count as income
            yearlyIncome += (action.costSavings || 0) * action.amount;
            
            // Add all scheme income for this year
            getAllIncomeTypes(action).forEach(incomeType => {
              yearlyIncome += calculateIncomeByType(action, year, incomeType.type);
            });
            
            // Annual maintenance costs (these are ongoing expenses)
            const yearlyMaintenanceCosts = (action.maintenanceCost || 0) * action.amount;
            
            // Net cash flow for this action this year = income - expenses
            yearlyNetCashFlow += yearlyIncome - yearlyMaintenanceCosts;
          });
          
          // Add this year's net cash flow to cumulative total
          cumulativeNetCashFlow += yearlyNetCashFlow;
          
          // Break-even occurs when cumulative net cash flow becomes positive
          // This means we've recovered the initial investment AND covered all ongoing costs
          if (cumulativeNetCashFlow >= 0 && breakEvenYear === null) {
            breakEvenYear = year;
          }
        }
        
        return breakEvenYear;
      }, [getAllIncomeTypes, calculateIncomeByType]);
  
    const totalImpact = useMemo(() => {
      const yearlyTotals = {};
      
      // Calculate upfront implementation costs
      const totalUpfrontCost = farmPlan.reduce((sum, action) => sum + ((action.implementationCost || 0) * action.amount), 0);
      
      // Calculate totals for years 1-5
      for (let year = 1; year <= 5; year++) {
        yearlyTotals[`year${year}`] = farmPlan.reduce((total, action) => {
          const incomeTypes = getAllIncomeTypes(action);
          let schemeIncome = 0;
    
          incomeTypes.forEach(incomeType => {
            schemeIncome += calculateIncomeByType(action, year, incomeType.type);
          });
    
          const costSavings = (action.costSavings || 0) * action.amount;
          const maintenanceCost = (action.maintenanceCost || 0) * action.amount;
          const implementationCost = year === 1 ? (action.implementationCost || 0) * action.amount : 0;
    
          return {
            costSavings: total.costSavings + costSavings,
            maintenanceCost: total.maintenanceCost + maintenanceCost,
            schemeIncome: total.schemeIncome + schemeIncome,
            implementationCost: total.implementationCost + implementationCost,
            totalIncome: total.totalIncome + schemeIncome,
            netIncome: total.netIncome + schemeIncome + costSavings - maintenanceCost - implementationCost
          };
        }, {
          costSavings: 0,
          maintenanceCost: 0,
          schemeIncome: 0,
          implementationCost: 0,
          totalIncome: 0,
          netIncome: 0
        });
      }
    
      // Calculate total amounts
      const totalAmount = farmPlan.reduce((sum, action) => sum + action.amount, 0);
      const totalAnnualCostSavings = farmPlan.reduce((sum, action) => sum + ((action.costSavings || 0) * action.amount), 0);
      const totalAnnualMaintenanceCost = farmPlan.reduce((sum, action) => sum + ((action.maintenanceCost || 0) * action.amount), 0);
      
      // Calculate break-even
      const breakEvenYear = calculateBreakEven(farmPlan);
      
      return {
        ...yearlyTotals,
        totalAmount,
        totalUpfrontCost,
        totalAnnualCostSavings,
        totalAnnualMaintenanceCost,
        breakEvenYear
      };
    }, [farmPlan, calculateIncomeByType, getAllIncomeTypes, calculateBreakEven]);
  
    const handleItemHover = (category, itemId, isHovering, event) => {
      if (isHovering) {
        setHoveredItem({ category, id: itemId });
  
        const selectedAction = Array.from(selectedItems.actions)[0];
        const selectedNature = Array.from(selectedItems.nature)[0];
  
        const x = event.clientX || (event.touches?.[0]?.clientX ?? 0);
        const y = event.clientY || (event.touches?.[0]?.clientY ?? 0);
  
        if (filteredView && selectedItems.actions.size > 0 && category === 'nature') {
          const explanation = getExplanation('actions', selectedAction, 'nature', itemId);
          if (explanation) {
            setExplanationText(explanation);
            setExplanationPosition({ x, y });
            setShowExplanation(true);
          }
        }
  
        if (filteredView && selectedItems.nature.size > 0 && category === 'resilience') {
          const explanation = getExplanation('nature', selectedNature, 'resilience', itemId);
          if (explanation) {
            setExplanationText(explanation);
            setExplanationPosition({ x, y });
            setShowExplanation(true);
          }
        }
      } else {
        setHoveredItem(null);
        setShowExplanation(false);
      }
    };
  
    const isConnectionSelected = (connection) => {
      const sourceSelected =
        (connection.type === 'nature-resilience' && selectedItems.nature.has(connection.sourceId)) ||
        (connection.type === 'action-nature' && selectedItems.actions.has(connection.sourceId));
      const targetSelected =
        (connection.type === 'nature-resilience' && selectedItems.resilience.has(connection.targetId)) ||
        (connection.type === 'action-nature' && selectedItems.nature.has(connection.targetId));
      return sourceSelected && targetSelected;
    };
  
    // Get all unique SAIQ codes from farm actions
    const getAllSAIQCodes = () => {
      const saiqCodes = new Set();
      Object.values(FARM_ACTION_DATA).forEach(action => {
        if (action.SAIQ) {
          action.SAIQ.forEach(code => saiqCodes.add(code));
        }
      });
      return Array.from(saiqCodes).sort();
    };
  
    const handleSAIQFilter = (saiqCode) => {
      setSelectedSAIQ(saiqCode);
      // Clear any existing selections when applying SAIQ filter
      setSelectedItems({ resilience: new Set(), nature: new Set(), actions: new Set() });
      setFilteredView(false);
      setVisibleItems({ resilience: new Set(), nature: new Set(), actions: new Set() });
    };
  
    const handleAECSFilter = (aecsType) => {
      setSelectedAECS(aecsType);
      // Clear any existing selections when applying AECS filter
      setSelectedItems({ resilience: new Set(), nature: new Set(), actions: new Set() });
      setFilteredView(false);
      setVisibleItems({ resilience: new Set(), nature: new Set(), actions: new Set() });
    };
  
    const clearAllSelections = () => {
      // Clear all filters
      setSelectedSAIQ('');
      setSelectedAECS('');
      // Clear item selections
      setSelectedItems({ resilience: new Set(), nature: new Set(), actions: new Set() });
      setFilteredView(false);
      setVisibleItems({ resilience: new Set(), nature: new Set(), actions: new Set() });
    };
  
    return (
      <div className="mural-app">
        {isMobile ? (
          <div className="mural-mobile-warning">
            <p>📱 This app is not available on small screens.</p>
            <p>Please use a larger screen (desktop or tablet) for the best experience.</p>
          </div>
        ) : (
          <>
            <div className="mural-header">
              <h1>FarmBalance Resilience Framework</h1>
              <p>Visualise the connected pathways from farm actions to potential on-farm resilience outcomes</p>
              <div className="mrv-info">
                <span className="mrv-badge">🛰️ Click a farm action or nature outcome to see MMRV details</span>
              </div>
              
              {/* Filters */}
              <div className="filters-container">
                <div className="saiq-filter">
                  <label htmlFor="saiq-select" className="filter-label">Filter by FSA Gold Question:</label>
                  <select
                    id="saiq-select"
                    value={selectedSAIQ}
                    onChange={(e) => handleSAIQFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">All actions</option>
                    {getAllSAIQCodes().map(code => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </select>
                </div>
                
                <div className="aecs-filter">
                  <label htmlFor="aecs-select" className="filter-label">Filter by AECS eligibility:</label>
                  <select
                    id="aecs-select"
                    value={selectedAECS}
                    onChange={(e) => handleAECSFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">All actions</option>
                    <option value="aecs">AECS eligible</option>
                    <option value="non-aecs">Non-AECS</option>
                  </select>
                </div>
                
                {(selectedSAIQ || selectedAECS || filteredView) && (
                  <button 
                    className="clear-filters-btn"
                    onClick={clearAllSelections}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
            
            <div className="mural-container" ref={containerRef}>
              <svg className="connection-svg" width="100%" height="100%">
                <defs>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge> 
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                
                {connections.map(connection => {
                  const sourceVisible = connection.type === 'action-nature' ? 
                    isItemVisible('actions', connection.sourceId) : 
                    isItemVisible('nature', connection.sourceId);
                  const targetVisible = connection.type === 'action-nature' ? 
                    isItemVisible('nature', connection.targetId) : 
                    isItemVisible('resilience', connection.targetId);
                  
                  if (!sourceVisible || !targetVisible) return null;
                  
                  const isHighlighted = isConnectionHighlighted(connection);
                  const isSelected = isConnectionSelected(connection);
                  const strokeColor = isHighlighted || isSelected ? '#374151' : '#d1d5db';
                  const strokeWidth = isHighlighted || isSelected ? 3 : 2;
                  const opacity = isHighlighted || isSelected ? 1 : 0.7;
                  
                  return (
                    <g key={connection.id}>
                      <path
                        d={createBezierPath(connection)}
                        stroke={strokeColor}
                        strokeWidth={strokeWidth}
                        fill="none"
                        opacity={opacity}
                        className={`connection-path ${isHighlighted ? 'highlighted' : ''} ${isSelected ? 'selected' : ''}`}
                      />
                    </g>
                  );
                })}
              </svg>
              
              <div className="mural-column actions-column">
                <h2>🚜 Farm Actions</h2>
                <div className="mural-items">
                  {FLOW_DATA.farmActions.map(item => (
                    <div key={item.id} className="mural-item-container">
                      <div
                        id={`action-${item.id}`}
                        className={`mural-item action-item ${isItemSelected('actions', item.id) ? 'selected' : ''} ${!isItemVisible('actions', item.id) ? 'hidden' : ''}`}
                        onClick={() => handleItemClick('actions', item.id)}
                        onMouseEnter={(e) => handleItemHover('actions', item.id, true, e)}
                        onMouseLeave={(e) => handleItemHover('actions', item.id, false, e)}
                      >
                        <div className="item-name">
                          {item.name}
                        </div>
                      </div>
                      
                      {isItemSelected('actions', item.id) && MRV_ACTION_DATA[item.id] && (
                        <div className="inline-mrv-info">
                          <div className="satellite-verification-inline">
                            <div className={`satellite-badge-inline ${MRV_ACTION_DATA[item.id].satelliteVerifiable ? 'verifiable' : 'not-verifiable'}`}>
                              🛰️ {MRV_ACTION_DATA[item.id].satelliteVerifiable ? 'Satellite Verifiable' : 'Not Satellite Verifiable'}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mural-column nature-column">
                <h2>🌿 Nature Outcomes*</h2>
                <div className="mural-items">
                  {FLOW_DATA.natureOutcomes.map(item => (
                    <div key={item.id} className="mural-item-container">
                      <div
                        id={`nature-${item.id}`}
                        className={`mural-item nature-item ${isItemSelected('nature', item.id) ? 'selected' : ''} ${!isItemVisible('nature', item.id) ? 'hidden' : ''}`}
                        style={{ borderColor: item.color }}
                        onClick={() => handleItemClick('nature', item.id)}
                        onMouseEnter={(e) => handleItemHover('nature', item.id, true, e)}
                        onMouseLeave={(e) => handleItemHover('nature', item.id, false, e)}
                      >
                        <div className="item-name">{item.name}</div>
                      </div>
                      
                      {isItemSelected('nature', item.id) && MRV_NATURE_DATA[item.id] && (
                        <div className="inline-mrv-info">
                          <div className="monitoring-methods-inline">
                            <h5>🔬 MMRV Options:</h5>
                            <ul className="monitoring-methods-list">
                            {MRV_NATURE_DATA[item.id].monitoringMethods.map((method, index) => (
                                <li key={index}>{method}</li>
                            ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mural-column resilience-column">
                <h2>🎯 Resilience Outcomes*</h2>
                <div className="mural-items">
                  {FLOW_DATA.resilienceOutcomes.map(item => (
                    <div
                      key={item.id}
                      id={`resilience-${item.id}`}
                      className={`mural-item resilience-item ${isItemSelected('resilience', item.id) ? 'selected' : ''} ${!isItemVisible('resilience', item.id) ? 'hidden' : ''}`}
                      style={{ borderColor: item.color }}
                      onClick={() => handleItemClick('resilience', item.id)}
                      onMouseEnter={(e) => handleItemHover('resilience', item.id, true, e)}
                      onMouseLeave={(e) => handleItemHover('resilience', item.id, false, e)}
                    >
                      <div className="item-name">{item.name}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="outcomes-note">
              * potential outcomes subject to context and variability
            </div>

            </div>
            
            {showExplanation && (
              <div 
                className="explanation-tooltip"
                style={{
                  left: explanationPosition.x + 10,
                  top: explanationPosition.y + 10
                }}
              >
                {explanationText}
              </div>
            )}
            
            <div className="farm-plan-section">
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ marginBottom: '8px' }}>🚜 Your Farm Plan</h3>
                <p style={{ marginBottom: '16px', color: '#64748b' }}>
                  Build up a resilience plan for your farm
                </p>
  
                <select
                  style={{
                    padding: '10px 12px',
                    fontSize: '0.95rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    background: '#fff',
                    maxWidth: '300px',
                    width: '100%'
                  }}
                  value=""
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    if (selectedId) {
                      addToFarmPlan(selectedId);
                    }
                  }}
                >
                  <option value="">Select an action to add</option>
                  {FLOW_DATA.farmActions.map(action => (
                    !farmPlan.find(a => a.id === action.id) && (
                      <option key={action.id} value={action.id}>
                        {action.name}
                        {FARM_ACTION_DATA[action.id]?.aecs && ' (AECS)'}
                        {FARM_ACTION_DATA[action.id]?.SAIQ && 
                          ` (${FARM_ACTION_DATA[action.id].SAIQ.join(', ')})`
                        }
                      </option>
                    )
                  ))}
                </select>
              </div>
              
              {farmPlan.length > 0 && (
                <>
                  <div className="farm-plan-items">
                    {farmPlan.map(action => (
                      <div key={action.id} className="farm-plan-item">
                        <div className="action-header">
                        <h4>
  {action.name}
  {action.notes && (
    <span style={{ fontWeight: 'normal', fontSize: '0.9rem', marginLeft: '8px', color: '#64748b' }}>
      ({action.notes})
    </span>
  )}
</h4>

                          <button 
                            className="remove-action"
                            onClick={() => removeFromFarmPlan(action.id)}
                          >
                            ✕
                          </button>
                        </div>
  
                        <div className="amount-input">
                          <label>Amount ({action.unit}):</label>
                          <input
                            type="number"
                            min="0"
                            step={action.unit === 'metre' ? '1' : '0.1'}
                            value={action.amount}
                            onChange={(e) => updateAmount(action.id, parseFloat(e.target.value) || 0)}
                            className="amount-field"
                          />
                          {action.AECS_maxarea && (
                            <span className="max-area-info">
                              (Max {action.AECS_maxarea} {action.unit} eligible for AECS income)
                            </span>
                          )}
                        </div>
  
                        <div className="action-economics">
                          <div className="economic-metric">
                            <span className="metric-label">Annual Cost Savings:</span>
                            <span className="metric-value">
                              £{((action.costSavings || 0) * action.amount).toLocaleString()}
                            </span>
                          </div>
                          <div className="economic-metric">
                            <span className="metric-label">Upfront Cost:</span>
                            <span className="metric-value">
                              £{((action.implementationCost || 0) * action.amount).toLocaleString()}
                            </span>
                          </div>
                          <div className="economic-metric">
                            <span className="metric-label">Annual Maintenance Cost:</span>
                            <span className="metric-value">
                              £{((action.maintenanceCost || 0) * action.amount).toLocaleString()}
                            </span>
                          </div>
                        </div>
  
                        {/* Grouped Income Section */}
                        <div className="grouped-income-section">
  <h5>Income Opportunities:</h5>
  <div className="income-schemes">
    {getAllIncomeTypes(action).length > 0 ? (
      getAllIncomeTypes(action).map((incomeType, index) => (
        <div key={index} className="income-scheme">
          <div className="scheme-header">
            <span className="scheme-name">{incomeType.label}</span>
            <div className="scheme-info">
              {incomeType.maxArea && (
                <span className="scheme-limit">
                  Max {incomeType.maxArea} {action.unit} eligible for income
                </span>
              )}
              {incomeType.maxYears && (
                <span className="scheme-duration">
                  {incomeType.maxYears} year{incomeType.maxYears > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <div className="scheme-payments-yearly">
            {[1, 2, 3, 4, 5].map(year => (
              <div key={year} className="payment-year">
                <span className="year-label">Year {year}:</span>
                <span className="payment-value">
                  £{calculateIncomeByType(action, year, incomeType.type).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))
    ) : (
      <p style={{ color: '#64748b', padding: '8px 0' }}>No income opportunities available.</p>
    )}
  </div>
</div>

  
                        
  
                        
                      </div>
                    ))}
                  </div>
  
                  <div className="total-impact">
                    <h4>📊 Total Farm Impact</h4>
                    <div className="total-metrics">
                      <div className="total-metric savings">
                        <span className="total-label">Total Annual Cost Savings</span>
                        <div className="total-value">
                          £{totalImpact.totalAnnualCostSavings.toLocaleString()}
                        </div>
                      </div>
                      <div className="total-metric cost">
                        <span className="total-label">Total Upfront Cost</span>
                        <div className="total-value">
                          £{totalImpact.totalUpfrontCost.toLocaleString()}
                        </div>
                      </div>
                      <div className="total-metric cost">
                        <span className="total-label">Total Annual Maintenance Cost</span>
                        <div className="total-value">
                          £{totalImpact.totalAnnualMaintenanceCost.toLocaleString()}
                        </div>
                      </div>
                      <div className="total-metric area">
                        <span className="total-label">Total Area/Length</span>
                        <div className="total-value">
                          {totalImpact.totalAmount.toLocaleString()} units
                        </div>
                      </div>
                    </div>
  
                    <div className="yearly-income-summary">
                      <h5>📈 Total Income by Year:</h5>
                      <div className="yearly-totals">
                        {[1, 2, 3, 4, 5].map(year => (
                          <div key={year} className="yearly-total-item">
                            <div className="year-header">
                              <span className="year-title">Year {year}</span>
                              <span className="year-net-value">
                                £{totalImpact[`year${year}`].netIncome.toLocaleString()}
                              </span>
                            </div>
                            <div className="year-breakdown">
                            <div className="breakdown-item">
                                <span>Scheme Income:</span>
                                <span>£{totalImpact[`year${year}`].schemeIncome.toLocaleString()}</span>
                            </div>
                            <div className="breakdown-item">
                                <span>Cost Savings:</span>
                                <span>£{totalImpact[`year${year}`].costSavings.toLocaleString()}</span>
                            </div>
                            <div className="breakdown-item cost">
                                <span>Maintenance Cost:</span>
                                <span>-£{totalImpact[`year${year}`].maintenanceCost.toLocaleString()}</span>
                            </div>
                            {year === 1 && totalImpact[`year${year}`].implementationCost > 0 && (
                                <div className="breakdown-item cost">
                                <span>Upfront Cost:</span>
                                <span>-£{totalImpact[`year${year}`].implementationCost.toLocaleString()}</span>
                                </div>
                            )}
                            </div>

                          </div>
                        ))}
                      </div>
                    </div>
  
                    {/* <div className="break-even-analysis">
                      <h5>💰 Break-Even Analysis:</h5>
                      <div className="break-even-result">
                        <span className="break-even-label">Time to Break Even:</span>
                        <span className="break-even-value">
                          {totalImpact.breakEvenYear 
                            ? `${totalImpact.breakEvenYear} year${totalImpact.breakEvenYear > 1 ? 's' : ''}`
                            : 'More than 10 years'
                          }
                        </span>
                      </div>
                    </div> */}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  }
  
  
export default App;